import { useEffect, useRef } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useSession } from '../contexts/SessionContext';
import { useNow } from '../hooks/useNow';
import useTexts from '../hooks/useTexts';

interface Props {
  /** Voláno (jednou) jakmile expiresAt přejde nulou. */
  onExpire: () => void;
}

const WARNING_MS = 5 * 60 * 1000;

export default function SessionCountdown({ onExpire }: Props) {
  const { isAuthenticated, expiresAt } = useSession();
  const now = useNow(1000);
  const texts = useTexts();
  const firedRef = useRef(false);

  const remainingMs =
    isAuthenticated && expiresAt !== null ? expiresAt - now : null;

  useEffect(() => {
    if (remainingMs !== null && remainingMs <= 0 && !firedRef.current) {
      firedRef.current = true;
      onExpire();
    }
    if (remainingMs !== null && remainingMs > 0) {
      firedRef.current = false;
    }
  }, [remainingMs, onExpire]);

  if (remainingMs === null || remainingMs <= 0) return null;

  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const display =
    hours > 0 ? `${hours}h ${pad(minutes)}m` : `${pad(minutes)}:${pad(seconds)}`;

  const warning = remainingMs < WARNING_MS;

  return (
    <Tooltip title={texts.SESSION_EXPIRES_IN_TOOLTIP}>
      <Box
        component="span"
        sx={{
          display: { xs: 'none', md: 'inline-flex' },
          alignItems: 'center',
          gap: 0.5,
          verticalAlign: 'middle',
          lineHeight: 1,
          color: warning ? 'error.main' : 'text.secondary',
        }}
      >
        <AccessTimeIcon fontSize="small" />
        <Typography
          variant="caption"
          sx={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            color: 'inherit',
          }}
        >
          {display}
        </Typography>
      </Box>
    </Tooltip>
  );
}
